/* TEVQA: load optional bulk/volume-price code only for real, non-template DOM. */
(() => {
  const config = document.currentScript?.dataset;
  if (!config || window.__tevqaComponentLoader) return;
  window.__tevqaComponentLoader = true;

  const features = [
    { key: 'quantityPopover', tag: 'quantity-popover', selector: 'quantity-popover' },
    { key: 'pricePerItem', tag: 'price-per-item', selector: 'price-per-item' },
    { key: 'quickAddBulk', tag: 'quick-add-bulk', selector: 'quick-add-bulk', dependencies: ['quantityPopover', 'pricePerItem'] },
    { key: 'quickOrderList', tag: 'quick-order-list', selector: 'quick-order-list, bulk-modal, quick-order-list-remove-button, quick-order-list-remove-all-button', dependencies: ['quantityPopover', 'pricePerItem'] }
  ];
  const requests = new Map();
  const selector = features.map(feature => feature.selector).join(',');
  const queue = new Set();
  let scheduled = false;

  function load(feature) {
    if (customElements.get(feature.tag)) return Promise.resolve();
    if (requests.has(feature.key)) return requests.get(feature.key);
    const request = (async () => {
      for (const key of feature.dependencies || []) await load(features.find(item => item.key === key));
      if (customElements.get(feature.tag)) return;
      const value = config[feature.key];
      if (!value) throw new Error(`Missing asset URL: ${feature.key}`);
      const url = new URL(value, document.baseURI).href;
      await new Promise((resolve, reject) => {
        let script = [...document.scripts].find(item => item.src === url && item.dataset.tevqaFailed !== 'true');
        const created = !script;
        if (created) {
          script = document.createElement('script');
          script.src = url;
          script.async = false;
          script.dataset.tevqaOptional = feature.key;
        }
        let timeout;
        const cleanup = () => {
          clearTimeout(timeout);
          script.removeEventListener('load', ready);
          script.removeEventListener('error', failed);
        };
        const ready = () => {
          cleanup();
          if (customElements.get(feature.tag)) resolve();
          else {
            script.dataset.tevqaFailed = 'true';
            if (created) script.remove();
            reject(new Error(`Component was not registered: ${feature.tag}`));
          }
        };
        const failed = () => {
          cleanup();
          script.dataset.tevqaFailed = 'true';
          if (created) script.remove();
          reject(new Error(`Could not load ${feature.key}`));
        };
        script.addEventListener('load', ready, { once: true });
        script.addEventListener('error', failed, { once: true });
        timeout = setTimeout(failed, 20000);
        if (created) document.head.appendChild(script);
      });
    })();
    requests.set(feature.key, request);
    request.catch(() => requests.delete(feature.key));
    return request;
  }

  function scan(root) {
    if (!(root instanceof Element) && root !== document) return;
    for (const feature of features) {
      if (customElements.get(feature.tag)) continue;
      if ((root instanceof Element && root.matches(feature.selector)) || root.querySelector(feature.selector)) queue.add(feature);
    }
    if (!queue.size || scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      const pending = [...queue];
      queue.clear();
      pending.forEach(feature => load(feature).catch(error => console.warn('[TEVQA components]', error)));
    });
  }

  // New recommendations, filters and quick-add content are examined once when
  // inserted. No scroll polling or repeated whole-document scans.
  const observer = new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(node => {
      if (node instanceof Element && node.tagName !== 'SCRIPT' && node.tagName !== 'STYLE') scan(node);
    }));
    if (features.every(feature => customElements.get(feature.tag))) observer.disconnect();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  scan(document);

  // Preserve the first button click / quantity change if a component arrives
  // immediately before its script is ready. Never intercept normal product links.
  for (const type of ['click', 'change']) {
    document.addEventListener(type, event => {
      if (!(event.target instanceof Element)) return;
      const element = event.target.closest(selector);
      if (!element) return;
      const feature = features.find(item => element.matches(item.selector));
      if (!feature || customElements.get(feature.tag)) return;
      const target = type === 'click' ? event.target.closest('button') : event.target;
      if (!target || (type === 'click' && event.target.closest('a[href]'))) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (target.dataset.tevqaWaiting === 'true') return;
      target.dataset.tevqaWaiting = 'true';
      element.setAttribute('aria-busy', 'true');
      load(feature).then(() => {
        if (!target.isConnected) return;
        if (type === 'click') target.click();
        else target.dispatchEvent(new Event('change', { bubbles: true }));
      }).catch(error => console.warn('[TEVQA components] Retry the control to retry loading.', error))
        .finally(() => {
          delete target.dataset.tevqaWaiting;
          element.removeAttribute('aria-busy');
        });
    }, true);
  }
})();
